import { useEffect, useMemo, useState } from 'react';

import { Icon } from '@ds/core';
import {
    COMPORTAMENTO_PADRAO, LIMITE_DAS_INSTRUCOES, LIMITE_DE_HABILIDADES, OPCOES_DE_DETALHE, OPCOES_DE_EMOJI, OPCOES_DE_ESTILO,
    OPCOES_DE_PROATIVIDADE, OPCOES_DE_TOM, resumoDoComportamento,
} from '@/data/brain';
import type { ComportamentoDoBrain, HabilidadeDoBrain, OpcaoDoCatalogo, PreferenciasDoBrain } from '@/data/brain';
import { s } from './classes';
import { Dialogo, DialogoCabecalho, DialogoCorpo, DialogoRodape } from './ui/camadas';

/**
 * Personalizar — porte do `BrainPersonalizeHub` e do `BrainPersonalizePage` do
 * Brain original, sem conectores MCP (fora do escopo). O que muda é onde fica:
 * no original, no navegador; aqui, no servidor (`brain_preferencias`), por pessoa
 * e clínica. A tela chama `aoMudar` a cada ajuste e quem a usa grava.
 */

export type SecaoDoPersonalizar = 'hub' | 'habilidades' | 'comportamento';

/* ------------------------------------------------------------- comportamento */

function Opcoes<T extends string>({ titulo, opcoes, valor, aoMudar }: { titulo: string; opcoes: OpcaoDoCatalogo<T>[]; valor: T; aoMudar: (v: T) => void }) {
    return (
        <div className={s.section} role="group" aria-label={titulo}>
            <div className={s.sectionTitle}>{titulo}</div>
            <div className={s.optionGrid}>
                {opcoes.map((o) => (
                    <button key={o.id} type="button" className={`${s.optionCard} ${valor === o.id ? s.optionCardActive : ''}`} aria-pressed={valor === o.id} onClick={() => aoMudar(o.id)}>
                        <div className={s.optionLabel}>{o.label}</div>
                        <div className={s.optionHint}>{o.hint}</div>
                    </button>
                ))}
            </div>
        </div>
    );
}

function Alternar({ rotulo, marcado, aoMudar }: { rotulo: string; marcado: boolean; aoMudar: (v: boolean) => void }) {
    return (
        <label className="brain-sub__switch-row">
            <span className="brain-sub__switch-label">{rotulo}</span>
            <input type="checkbox" className="brain-sub__switch-input" checked={marcado} onChange={(e) => aoMudar(e.target.checked)} />
        </label>
    );
}

export interface PaginaDeComportamentoProps {
    preferencias: PreferenciasDoBrain;
    aoMudar: (proximas: PreferenciasDoBrain) => void;
    aoSalvar: () => void;
    aoRestaurar: () => void;
    somenteLeitura: boolean;
}

export function PaginaDeComportamento({ preferencias, aoMudar, aoSalvar, aoRestaurar, somenteLeitura }: PaginaDeComportamentoProps) {
    const c = preferencias.comportamento;
    const mudar = (parcial: Partial<ComportamentoDoBrain>) => aoMudar({ ...preferencias, comportamento: { ...c, ...parcial } });
    const resumo = useMemo(() => resumoDoComportamento(preferencias), [preferencias]);

    return (
        <div className={s.root}>
            <div className={s.inner}>
                <div className={s.headerRow}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <h2 className={s.title}>Personalizar</h2>
                        <p className={s.lead}>
                            Defina como a VecchIA deve conversar e se comportar com você. As
                            preferências ficam guardadas na sua conta e valem para as próximas mensagens.
                        </p>
                    </div>
                    <div className={s.actions}>
                        <button type="button" className={s.btnGhost} onClick={aoRestaurar} disabled={somenteLeitura}>
                            <Icon name="rotate-ccw" size={14} />
                            Restaurar padrão
                        </button>
                        <button type="button" className={s.btnPrimary} onClick={aoSalvar} disabled={somenteLeitura}>
                            <Icon name="save" size={14} />
                            Salvar preferências
                        </button>
                    </div>
                </div>

                <fieldset disabled={somenteLeitura} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                    <Opcoes titulo="Tom de conversa" opcoes={OPCOES_DE_TOM} valor={c.toneStyle} aoMudar={(toneStyle) => mudar({ toneStyle })} />
                    <Opcoes titulo="Nível de detalhe" opcoes={OPCOES_DE_DETALHE} valor={c.verbosity} aoMudar={(verbosity) => mudar({ verbosity })} />
                    <Opcoes titulo="Proatividade" opcoes={OPCOES_DE_PROATIVIDADE} valor={c.proactivity} aoMudar={(proactivity) => mudar({ proactivity })} />
                    <Opcoes titulo="Estilo de conversa" opcoes={OPCOES_DE_ESTILO} valor={c.conversationStyle} aoMudar={(conversationStyle) => mudar({ conversationStyle })} />
                    <Opcoes titulo="Uso de emojis" opcoes={OPCOES_DE_EMOJI} valor={c.emojiUsage} aoMudar={(emojiUsage) => mudar({ emojiUsage })} />

                    <div className={s.section}>
                        <div className={s.sectionTitle}>Comportamento</div>
                        <div className="brain-sub__toggles-card">
                            <Alternar rotulo="Fazer perguntas quando faltar informação essencial" marcado={c.askClarifyingQuestions} aoMudar={(askClarifyingQuestions) => mudar({ askClarifyingQuestions })} />
                            <Alternar rotulo="Usar listas e tópicos para organizar respostas" marcado={c.useBulletPoints} aoMudar={(useBulletPoints) => mudar({ useBulletPoints })} />
                        </div>
                    </div>

                    <div className={s.section}>
                        <label className={s.sectionTitle} htmlFor="brain-instrucoes">Instruções personalizadas</label>
                        <textarea
                            id="brain-instrucoes" className={s.textarea} rows={4} maxLength={LIMITE_DAS_INSTRUCOES}
                            placeholder="Ex.: Sempre me chame pelo primeiro nome. Priorize a agenda de amanhã. Evite jargões técnicos..."
                            value={preferencias.instrucoes}
                            onChange={(e) => aoMudar({ ...preferencias, instrucoes: e.target.value })}
                        />
                        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--brain-text-secondary)', textAlign: 'right' }}>
                            {String(preferencias.instrucoes.length)}/{String(LIMITE_DAS_INSTRUCOES)}
                        </div>
                    </div>
                </fieldset>

                <div className={s.preview}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <Icon name="sparkles" size={14} />
                        <strong style={{ fontSize: 12, color: 'var(--brain-text)' }}>Resumo ativo</strong>
                    </div>
                    {resumo}
                </div>
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- habilidades */

export interface HabilidadesProps {
    preferencias: PreferenciasDoBrain;
    aoMudar: (proximas: PreferenciasDoBrain) => void;
    somenteLeitura: boolean;
}

function novaHabilidade(): HabilidadeDoBrain {
    return { id: `habilidade-${Date.now().toString(36)}`, nome: 'Nova habilidade', descricao: '', ativa: true };
}

export function Habilidades({ preferencias, aoMudar, somenteLeitura }: HabilidadesProps) {
    const lista = preferencias.habilidades;
    const [selecionada, setSelecionada] = useState<string | null>(lista[0]?.id ?? null);
    const [busca, setBusca] = useState('');
    const [apagando, setApagando] = useState<HabilidadeDoBrain | null>(null);

    useEffect(() => {
        if (!selecionada && lista[0]) setSelecionada(lista[0].id);
        if (selecionada && !lista.some((h) => h.id === selecionada)) setSelecionada(lista[0]?.id ?? null);
    }, [lista, selecionada]);

    const filtradas = useMemo(() => {
        const q = busca.trim().toLowerCase();
        return q ? lista.filter((h) => h.nome.toLowerCase().includes(q) || h.descricao.toLowerCase().includes(q)) : lista;
    }, [lista, busca]);
    const atual = lista.find((h) => h.id === selecionada) ?? null;
    const noLimite = lista.length >= LIMITE_DE_HABILIDADES;

    const trocar = (id: string, parcial: Partial<HabilidadeDoBrain>) => {
        aoMudar({ ...preferencias, habilidades: lista.map((h) => (h.id === id ? { ...h, ...parcial } : h)) });
    };
    const adicionar = () => {
        if (noLimite) return;
        const nova = novaHabilidade();
        aoMudar({ ...preferencias, habilidades: [nova, ...lista] });
        setSelecionada(nova.id);
    };

    return (
        <div className="brain-pers-hub__split">
            <aside className="brain-pers-hub__skills-list" aria-label="Habilidades pessoais">
                <div className="brain-pers-hub__skills-list-head">
                    <span>Habilidades pessoais</span>
                    <div className="brain-pers-hub__skills-list-actions">
                        <button type="button" aria-label="Nova habilidade" onClick={adicionar} disabled={somenteLeitura || noLimite} title={noLimite ? `O limite é ${String(LIMITE_DE_HABILIDADES)} habilidades.` : undefined}>
                            <Icon name="plus" size={14} />
                        </button>
                    </div>
                </div>
                <div className="brain-pers-hub__skills-search">
                    <input type="search" placeholder="Buscar habilidades..." aria-label="Buscar habilidades" value={busca} onChange={(e) => setBusca(e.target.value)} />
                </div>
                <div className="brain-pers-hub__skills-items">
                    {filtradas.map((h) => (
                        <button key={h.id} type="button" className={`brain-pers-hub__skill-item${selecionada === h.id ? ' brain-pers-hub__skill-item--active' : ''}`} onClick={() => setSelecionada(h.id)} aria-current={selecionada === h.id ? 'true' : undefined}>
                            <Icon name="file-text" size={14} />
                            <span>{h.nome}</span>
                        </button>
                    ))}
                </div>
            </aside>

            <div className="brain-pers-hub__skill-detail">
                {atual ? (
                    <>
                        <div className="brain-pers-hub__skill-detail-head">
                            <h3>{atual.nome}</h3>
                            <div className="brain-pers-hub__skill-detail-actions">
                                <label className="brain-pers-hub__toggle">
                                    <span className="brain-so-leitor">{atual.ativa ? 'Desativar a habilidade' : 'Ativar a habilidade'}</span>
                                    <input type="checkbox" checked={atual.ativa} disabled={somenteLeitura} onChange={() => trocar(atual.id, { ativa: !atual.ativa })} />
                                    <span className="brain-pers-hub__toggle-track" />
                                </label>
                            </div>
                        </div>
                        <div className="brain-pers-hub__skill-meta">
                            <div>
                                <span className="brain-pers-hub__meta-label">Adicionado por</span>
                                <span>Você</span>
                            </div>
                            <div>
                                <span className="brain-pers-hub__meta-label">Situação</span>
                                <span>{atual.ativa ? 'Ativa: entra em toda resposta' : 'Desativada'}</span>
                            </div>
                        </div>
                        <fieldset disabled={somenteLeitura} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                            <div className={s.section}>
                                <label className={s.sectionTitle} htmlFor="brain-habilidade-nome">Nome da habilidade</label>
                                <input id="brain-habilidade-nome" className={s.textarea} maxLength={80} value={atual.nome} onChange={(e) => trocar(atual.id, { nome: e.target.value })} />
                            </div>
                            <div className={s.section}>
                                <label className={s.sectionTitle} htmlFor="brain-habilidade-descricao">Descrição</label>
                                <textarea
                                    id="brain-habilidade-descricao" className={s.textarea} rows={6} maxLength={500} value={atual.descricao}
                                    onChange={(e) => trocar(atual.id, { descricao: e.target.value })}
                                    placeholder="Descreva o processo, as normas e o conhecimento que a VecchIA deve seguir quando esta habilidade estiver ativa..."
                                />
                                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--brain-text-secondary)', textAlign: 'right' }}>{String(atual.descricao.length)}/500</div>
                            </div>
                        </fieldset>
                        <div className="brain-pers-hub__skill-footer">
                            <button type="button" className={`${s.btnGhost} brain-pers-hub__danger-btn`} disabled={somenteLeitura} onClick={() => setApagando(atual)}>
                                <Icon name="trash-2" size={14} />
                                Excluir habilidade
                            </button>
                        </div>
                    </>
                ) : (
                    <div className={s.empty}>Crie sua primeira habilidade.</div>
                )}
            </div>

            <Dialogo aberto={apagando !== null} aoFechar={() => setApagando(null)} rotulo="Excluir esta habilidade?" tamanho="md">
                <DialogoCabecalho>
                    <h2 className={s.dialogTitle}><Icon name="trash-2" size={18} />Excluir esta habilidade?</h2>
                    <p className={s.dialogLead}>“{apagando?.nome}” deixa de valer nas próximas respostas. Não dá para desfazer.</p>
                </DialogoCabecalho>
                <DialogoCorpo><span /></DialogoCorpo>
                <DialogoRodape>
                    <button type="button" className={s.btnGhost} onClick={() => setApagando(null)}>Cancelar</button>
                    <button
                        type="button" className={s.btnPrimary}
                        onClick={() => {
                            if (apagando) aoMudar({ ...preferencias, habilidades: lista.filter((h) => h.id !== apagando.id) });
                            setApagando(null);
                        }}
                    >
                        Excluir habilidade
                    </button>
                </DialogoRodape>
            </Dialogo>
        </div>
    );
}

/* ---------------------------------------------------------------- central */

export interface CentralDePersonalizacaoProps {
    secao: SecaoDoPersonalizar;
    aoTrocarSecao: (secao: SecaoDoPersonalizar) => void;
    preferencias: PreferenciasDoBrain;
    aoMudar: (proximas: PreferenciasDoBrain) => void;
    aoSalvar: () => void;
    somenteLeitura: boolean;
}

export function CentralDePersonalizacao({ secao, aoTrocarSecao, preferencias, aoMudar, aoSalvar, somenteLeitura }: CentralDePersonalizacaoProps) {
    const navegacao: { id: SecaoDoPersonalizar; icone: string; rotulo: string }[] = [
        { id: 'habilidades', icone: 'file-text', rotulo: 'Habilidades' },
        { id: 'comportamento', icone: 'sliders-horizontal', rotulo: 'Comportamento' },
    ];

    return (
        <div className="brain-pers-hub">
            <aside className="brain-pers-hub__sidebar">
                <button type="button" className="brain-pers-hub__sidebar-back" onClick={() => aoTrocarSecao('hub')}>
                    <Icon name="arrow-left" size={14} />
                    Personalizar
                </button>
                <nav className="brain-pers-hub__nav" aria-label="Seções do Personalizar">
                    {navegacao.map((item) => (
                        <button key={item.id} type="button" className={`brain-pers-hub__nav-item${secao === item.id ? ' brain-pers-hub__nav-item--active' : ''}`} onClick={() => aoTrocarSecao(item.id)} aria-current={secao === item.id ? 'page' : undefined}>
                            <Icon name={item.icone} size={15} />
                            {item.rotulo}
                        </button>
                    ))}
                </nav>
            </aside>

            <main className="brain-pers-hub__main">
                {secao === 'hub' && (
                    <div className="brain-pers-hub__landing">
                        <div className="brain-pers-hub__landing-icon"><Icon name="wrench" size={28} /></div>
                        <h2 className="brain-pers-hub__landing-title">Personalizar a VecchIA</h2>
                        <p className="brain-pers-hub__landing-sub">Habilidades e comportamento moldam como a VecchIA trabalha com você.</p>
                        <div className="brain-pers-hub__cards">
                            <button type="button" className="brain-pers-hub__card" onClick={() => aoTrocarSecao('habilidades')}>
                                <span className="brain-pers-hub__card-icon"><Icon name="file-text" size={18} /></span>
                                <span className="brain-pers-hub__card-body">
                                    <strong>Criar novas habilidades</strong>
                                    <span>Ensine à VecchIA os processos, as normas da equipe e o conhecimento da clínica.</span>
                                </span>
                                <span className="brain-pers-hub__card-arrow" style={{ display: 'inline-flex' }}><Icon name="chevron-right" size={16} /></span>
                            </button>
                            <button type="button" className="brain-pers-hub__card" onClick={() => aoTrocarSecao('comportamento')}>
                                <span className="brain-pers-hub__card-icon"><Icon name="sliders-horizontal" size={18} /></span>
                                <span className="brain-pers-hub__card-body">
                                    <strong>Ajustar o comportamento</strong>
                                    <span>Tom, nível de detalhe, emojis e instruções personalizadas.</span>
                                </span>
                                <span className="brain-pers-hub__card-arrow" style={{ display: 'inline-flex' }}><Icon name="chevron-right" size={16} /></span>
                            </button>
                        </div>
                    </div>
                )}
                {secao === 'habilidades' && <Habilidades preferencias={preferencias} aoMudar={aoMudar} somenteLeitura={somenteLeitura} />}
                {secao === 'comportamento' && (
                    <PaginaDeComportamento
                        preferencias={preferencias} aoMudar={aoMudar} aoSalvar={aoSalvar} somenteLeitura={somenteLeitura}
                        aoRestaurar={() => aoMudar({ ...preferencias, comportamento: COMPORTAMENTO_PADRAO, instrucoes: '' })}
                    />
                )}
            </main>
        </div>
    );
}
